import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '@env/environment'
import { Observable, catchError, map, of } from 'rxjs'

/** Un arrendamiento vivo del cliente. Solo folio: la capacidad se pide aparte y solo cuando hace falta. */
export interface BatchLeaseOption {
	leaseId: string
	folio: string
}

/** Una fila del archivo, leída y resuelta contra la base. Nada de esto está escrito todavía. */
export interface ParsedBatchRow {
	rowNumber: number
	status: 'LISTA' | 'ERROR' | 'POSIBLE_DUPLICADO'
	message: string
	rfc: string
	clientId: string | null
	clientName: string
	clientEmail: string
	accountId: string | null
	accountAlias: string
	amount: number
	paymentMethod: string
	trackingKey: string
	date: string | null
	activeLeases: BatchLeaseOption[]
	twins: any[]
}

export interface PaymentBatchParseResult {
	uploadBatch: string
	fileName: string
	totalRows: number
	readyCount: number
	errorCount: number
	duplicateCount: number
	totalAmount: number
	alreadyLoaded: number
	rows: ParsedBatchRow[]
}

/** Una fila del reporte del alta. Es la que trae el id del pago que se acaba de crear. */
export interface RegisteredBatchRow {
	rowNumber: number
	status: 'SUCCESS' | 'ERROR' | 'POSIBLE DUPLICADO'
	clientRfc: string
	clientName: string
	clientEmail: string
	clientId?: string
	amount: number
	paymentMethod: string
	trackingKey: string
	paymentDate: string
	message: string
	paymentId?: string
}

export interface PaymentBatchRegisterResult {
	message: string
	totalProcessed: number
	totalAmount: number
	totalErrors: number
	totalDuplicates: number
	payments: string[]
	uploadBatch: string
	rows: RegisteredBatchRow[]
}

export interface PaymentBatchState {
	uploadBatch: string
	payments: { paymentId: string; clientId: string; clientName: string; amount: number; pendingToApply: number; status: string; trackingKey: string; date: string }[]
	notice: { sentAt: string; sentTo: number; message: string } | null
}

export interface PaymentBatchNoticeResult {
	uploadBatch: string
	total: number
	sent: number
	failed: number
	recipients: { clientId: string; clientName: string; sent: boolean; reason?: string }[]
}

type Wrapped<T> = { error: boolean; msg: string; data: T | null }

/**
 * El lote de la carga masiva: leerlo, registrarlo, recuperarlo y avisar.
 *
 * Vive aparte de `ApiPaymentService` porque aquel muta `this.path` en casi cada método —un patrón
 * heredado que obliga a recordar en qué quedó la ruta— y porque estas cuatro llamadas son un flujo
 * con un orden, no cuatro operaciones sueltas sobre pagos.
 */
@Injectable({ providedIn: 'root' })
export class ApiPaymentBatchService {
	private readonly http = inject(HttpClient)

	/**
	 * Lee el archivo y devuelve qué pasaría. **No escribe nada**: se puede llamar tantas veces como
	 * haga falta mientras el operador corrige su hoja.
	 */
	parse(file: File): Observable<Wrapped<PaymentBatchParseResult>> {
		const form = new FormData()
		form.append('file', file, file.name)

		return this.http.post(`${environment.apiUrl}payments/parse-many`, form).pipe(
			map((r) => r as Wrapped<PaymentBatchParseResult>),
			catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo leer el archivo', data: null }))
		)
	}

	/**
	 * Registra los pagos del archivo. **Punto de no retorno.**
	 *
	 * Se manda el ARCHIVO otra vez y no las filas que devolvió la lectura: así el alta vuelve a
	 * resolver todo con su propio código y la huella del lote se calcula sobre el mismo contenido, que
	 * es lo que sostiene el guardia anti-recarga. Mandar filas ya digeridas dejaría que la pantalla
	 * decidiera montos.
	 */
	register(file: File): Observable<Wrapped<PaymentBatchRegisterResult>> {
		const form = new FormData()
		form.append('file', file, file.name)

		return this.http.post(`${environment.apiUrl}payments/create-many?format=json`, form).pipe(
			map((r) => r as Wrapped<PaymentBatchRegisterResult>),
			catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudieron registrar los pagos', data: null }))
		)
	}

	/** Los pagos de un lote ya registrado. Es lo que permite retomar el asistente por su ruta. */
	findBatch(uploadBatch: string): Observable<Wrapped<PaymentBatchState>> {
		return this.http.get(`${environment.apiUrl}payments/batch/${uploadBatch}`).pipe(
			map((r) => r as Wrapped<PaymentBatchState>),
			catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo recuperar el lote', data: null }))
		)
	}

	/** Avisa a los clientes del lote: un mensaje por cliente y una sola vez (D2). */
	notify(uploadBatch: string): Observable<Wrapped<PaymentBatchNoticeResult>> {
		return this.http.post(`${environment.apiUrl}payments/batch/${uploadBatch}/notify`, {}).pipe(
			map((r) => r as Wrapped<PaymentBatchNoticeResult>),
			catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudieron mandar los avisos', data: null }))
		)
	}
}
